<template>
  <div ref="quillEditorRef" class="quill-editor-container">
    <!-- 显示上传状态 -->
    <div v-if="localUploading" class="upload-status">正在上传, 请稍后...</div>

    <!-- Quill 编辑器 -->
    <quill-editor
      ref="quillEditor"
      v-model="content"
      :style="{ height: '450px' }"
      @blur="onEditorBlur"
      @focus="onEditorFocus"
      @ready="onEditorReady"
    ></quill-editor>
  </div>
</template>

<script>
import { Toast } from "vant";
import { uploadFile, deleteFile } from "../../api/upload";

export default {
  props: {
    value: String, // 绑定 v-model
    storeId: { type: Number, required: true }, // 店铺 ID
    uploading: Boolean, // 由父组件控制的上传状态
    readonly: Boolean,  // 新增 readonly 控制编辑器是否只读
  },
  data() {
    return {
      content: this.value, // 富文本内容
      localUploading: this.uploading, // 本地上传状态，避免直接修改 props
      uploadProgress: 0, // 上传进度
    };
  },
  watch: {
    value(newVal) {
      this.content = newVal;
    },
    content(newVal) {
      this.$emit("input", newVal); // 更新父组件的 v-model
    },
    uploading(newVal) {
      this.localUploading = newVal; // **同步外部上传状态**
    },
  },
  mounted() {
    /*
    this.$nextTick(() => {
      this.adjustEditorHeight();
    });
    */
  },
  methods: {
    /** 监听 Quill 编辑器初始化 */
    onEditorReady(quill) {
      if (!quill) {
        console.warn("Quill 编辑器未正确加载");
        return;
      }
      this.quillEditor = quill;

      // === 防止自动聚焦引发页面滚动 ===
      setTimeout(() => {
        try {
          quill.blur(); // 主动取消焦点，避免页面滚动
        } catch (e) {
          console.warn('quill.blur() 执行失败:', e);
        }
      }, 100); // 增加延迟时间，确保在扫码返回时能够防止自动聚焦

      if (this.readonly) {
        const toolbar = quill.getModule("toolbar");
        if (toolbar) {
          toolbar.container.style.display = "none"; // 隐藏工具栏
        }
      } else {
        // 确保 toolbar 模块已加载
        const toolbar = quill.getModule("toolbar");
        if (toolbar) {
          toolbar.addHandler("image", this.imageHandler);
        }
      }

    },

    /** 监听 Quill 失焦 */
    onEditorBlur() {
      console.log("blur", this.content);
    },

    /** 监听 Quill 聚焦 */
    onEditorFocus() {
      console.log("focus", this.content);
    },

    /** 调整编辑器高度 */
    adjustEditorHeight() {
      this.$nextTick(() => {
        const wrapper = this.$refs.quillEditorRef;
        if (!wrapper || !this.$refs.quillEditor || !this.$refs.quillEditor.$el) {
          console.warn("Quill 编辑器未正确加载");
          return;
        }
        const editor = this.$refs.quillEditor.$el.querySelector(".ql-editor");
        if (!editor) {
          console.warn("Quill DOM 未找到");
          return;
        }

        wrapper.style.height = "auto";
        const contentHeight = editor.scrollHeight;
        wrapper.style.height = `${Math.min(contentHeight, 500)}px`;
      });
    },

    /** 处理图片上传（手动选择或粘贴） */
    async imageHandler() {
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("multiple", "multiple");
      input.setAttribute("accept", "image/*"); // 限制只能上传图片
      input.click();

      input.onchange = async () => {
        const files = input.files;
        if (files && files.length > 0) {
          try {
            await this.uploadImages(files);
          } catch (error) {
            console.error("图片上传失败:", error);
          }
        }
      };
    },

    /** 上传图片到服务器，并插入 Quill 编辑器 */
    async uploadImages(files) {
      this.localUploading = true;
      this.$emit("update:uploading", true); // **通知父组件正在上传**

      Toast.loading({
        message: "上传中...",
        forbidClick: true,
        duration: 0,
      });

      try {
        const uploadPromises = Array.from(files).map(async (file) => {
          // **确保 `file` 是 Blob**
          if (!(file instanceof Blob)) {
            console.error("无效的文件类型:", file);
            return null;
          }

          const response = await uploadFile(this.storeId, file);

          // **检查 response 是否有效**
          if (!response || response.code === undefined) {
            console.error("上传失败: 响应为空或格式不正确", response);
            return null;
          }

          if (response.code === 0 && response.url) {
            this.insertImageToEditor(response.url);
          } else {
            Toast.fail(response.msg || "部分图片上传失败");
          }
        });

        await Promise.all(uploadPromises);
        Toast.success("上传完成");
      } catch (error) {
        console.error("上传发生异常：", error);
        Toast.fail("上传异常");
      } finally {
        Toast.clear();
        this.localUploading = false;
        this.$emit("update:uploading", false); // **通知父组件上传完成**
      }
    },

    /** 插入图片到 Quill 编辑器 */
    insertImageToEditor(imageUrl) {
      if (!this.quillEditor) {
        console.error("Quill 编辑器未初始化");
        return;
      }

      const range = this.quillEditor.getSelection();
      const index = range ? range.index : this.quillEditor.getLength(); // **如果没有选区，则插入到末尾**

      this.quillEditor.insertEmbed(index, "image", imageUrl);
      this.quillEditor.setSelection(index + 1);
      this.adjustEditorHeight();
    },
  },
};
</script>
<style lang="scss" scoped>
/* 确保 Quill 编辑器容器足够大 */
.quill-editor-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 550px;  /* 确保工具栏 + 编辑区域的高度 */
  max-height: 80vh;    /* 限制最大高度，避免超出视窗 */
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: auto;    /* 确保不会溢出 */

}

/* 确保工具栏固定在顶部 */
.ql-toolbar {
  position: sticky;
  top: 0;
  z-index: 10; /* 确保工具栏在内容之上 */
  background-color: #fff; /* 避免滚动时工具栏背景消失 */
}


/* Quill 编辑器正文部分 */
.ql-container {
  flex-grow: 1;      /* 让编辑区域填充剩余空间 */
  min-height: 450px; /* 确保文本区域最少 450px */
  overflow-y: auto;  /* 启用滚动，避免内容超出 */
}

/* 上传状态样式 */
.upload-status {
  margin-top: 10px;
  color: #ff4d4f;
  font-size: 16px;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/MyQuillEditor/MyQuillEditor.vue